import type { LockerPrepareRequest } from '@aquariusdefi/sdk-phase1';
import { Address, Contract, StrKey, xdr } from '@stellar/stellar-sdk-15';

import { FRACTION_DEN, getIceDelegationConfig } from 'constants/ice-delegation';

import { getNetworkPassphrase } from 'helpers/env';

import type {
    DelegationFractionsByProxy,
    EnabledIceDelegationConfig,
    WritableIceProxy,
} from 'types/ice-delegation';

type ProxyMethod = 'delegate' | 'undelegate';

export const createProxyRequest = (
    owner: string,
    proxies: WritableIceProxy[],
    destination: string,
    // One fraction for every proxy, or a fraction per proxy; omitted for undelegation.
    fraction?: number | DelegationFractionsByProxy,
    config: EnabledIceDelegationConfig = getIceDelegationConfig(),
): LockerPrepareRequest => {
    if (
        !StrKey.isValidEd25519PublicKey(owner) ||
        owner === destination ||
        (!StrKey.isValidEd25519PublicKey(destination) && !StrKey.isValidContract(destination))
    ) {
        throw new Error('Invalid delegation address.');
    }
    if (
        !proxies.length ||
        proxies.length > 2 ||
        new Set(proxies).size !== proxies.length ||
        proxies.some(proxy => proxy !== 'upvote' && proxy !== 'govern')
    ) {
        throw new Error('Select upvoteICE, governICE or both.');
    }
    const fractionOf = (proxy: WritableIceProxy) =>
        typeof fraction === 'object' ? fraction[proxy] : fraction;
    if (
        fraction !== undefined &&
        proxies.some(proxy => {
            const value = fractionOf(proxy);
            return !Number.isSafeInteger(value) || value <= 0 || value > FRACTION_DEN;
        })
    )
        throw new Error('Delegation share is invalid.');
    const method: ProxyMethod = fraction === undefined ? 'undelegate' : 'delegate';
    const argsOf = (proxy: WritableIceProxy) => [
        new Address(owner).toScVal(),
        new Address(destination).toScVal(),
        ...(fraction === undefined ? [] : [xdr.ScVal.scvU32(fractionOf(proxy))]),
    ];
    const selected = [...proxies].sort();
    if (selected.length === 1)
        return {
            source: owner,
            owner,
            contract: config.proxies[selected[0]],
            calls: [{ method, args: argsOf(selected[0]).map(arg => arg.toXDR('base64')) }],
        };
    const batchArgs = [
        xdr.ScVal.scvVec([new Address(owner).toScVal()]),
        xdr.ScVal.scvVec(
            selected.map(proxy =>
                xdr.ScVal.scvVec([
                    new Address(config.proxies[proxy]).toScVal(),
                    xdr.ScVal.scvSymbol(method),
                    xdr.ScVal.scvVec(argsOf(proxy)),
                ]),
            ),
        ),
        xdr.ScVal.scvBool(false),
    ];
    return {
        source: owner,
        owner,
        contract: config.batch,
        calls: [{ method: 'batch', args: batchArgs.map(arg => arg.toXDR('base64')) }],
    };
};

// The shared live-RPC lifecycle accepts only the exact ordinary delegation shapes
// produced above. It cannot invoke admin, migration, ICE-role or arbitrary batch calls.
export const proxyInvocation = (request: LockerPrepareRequest, passphrase: string) => {
    const config = getIceDelegationConfig();
    if (
        passphrase !== getNetworkPassphrase() ||
        request.source !== request.owner ||
        request.calls.length !== 1 ||
        request.batcher
    )
        throw new Error('Unsupported delegation request.');
    const call = request.calls[0];
    const args = call.args.map(arg => xdr.ScVal.fromXDR(arg, 'base64'));
    let targets: { contract: string; method: string; args: xdr.ScVal[] }[];
    if (request.contract === config.batch && call.method === 'batch') {
        if (
            args.length !== 3 ||
            args[0].switch().name !== 'scvVec' ||
            args[0].vec()?.length !== 1 ||
            args[1].switch().name !== 'scvVec' ||
            args[1].vec()?.length !== 2 ||
            args[2].switch().name !== 'scvBool' ||
            args[2].b() ||
            Address.fromScVal(args[0].vec()[0]).toString() !== request.owner
        )
            throw new Error('Unsupported delegation batch.');
        targets = args[1].vec().map(row => {
            if (row.switch().name !== 'scvVec' || row.vec()?.length !== 3)
                throw new Error('Unsupported delegation batch.');
            const [address, method, values] = row.vec();
            if (
                method.switch().name !== 'scvSymbol' ||
                values.switch().name !== 'scvVec' ||
                !values.vec()
            )
                throw new Error('Unsupported delegation batch.');
            return {
                contract: Address.fromScVal(address).toString(),
                method: method.sym().toString(),
                args: values.vec(),
            };
        });
    } else targets = [{ contract: request.contract, method: call.method, args }];
    const proxies = targets.map(target => {
        const proxy = (['upvote', 'govern'] as const).find(
            role => config.proxies[role] === target.contract,
        );
        if (
            !proxy ||
            !['delegate', 'undelegate'].includes(target.method) ||
            target.args.length !== (target.method === 'delegate' ? 3 : 2) ||
            Address.fromScVal(target.args[0]).toString() !== request.owner ||
            (target.method === 'delegate' && target.args[2].switch().name !== 'scvU32')
        )
            throw new Error('Unsupported delegation call.');
        return proxy;
    });
    const first = targets[0];
    const expected = createProxyRequest(
        request.owner,
        proxies,
        Address.fromScVal(first.args[1]).toString(),
        first.method === 'delegate'
            ? Object.fromEntries(
                  targets.map((target, index) => [proxies[index], target.args[2]?.u32()]),
              )
            : undefined,
        config,
    );
    if (JSON.stringify(request) !== JSON.stringify(expected))
        throw new Error('Delegation call does not match the selected operation.');
    return new Contract(request.contract).call(call.method, ...args);
};
